/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React from 'react';
import { styled } from '@superset-ui/core';

export interface DatasourceCellProps {
  /** 完整的数据集名称，格式通常为 database.table_name */
  name?: string;
  /** 单独指定数据集名称 */
  dataset?: string;
  /** 单独指定数据库名称 */
  database?: string;
  /** 是否显示左侧数据集图标，数据集管理页等已有类型图标的场景可关闭 */
  showIcon?: boolean;
  /** 是否显示名称后的 ↗ 标识，选择类交互（如更换数据集）可关闭 */
  showExtIcon?: boolean;
}

const DatasourceCellWrapper = styled.div`
  ${({ theme }) => `
    display: flex;
    align-items: center;
    gap: ${theme.gridUnit * 2}px;
    min-width: 0;
    padding: ${theme.gridUnit}px 0;
    margin: -${theme.gridUnit}px 0;
    border-radius: 4px;
    transition: background 0.15s ease;

    &:hover {
      background: ${theme.colors.grayscale.light4};
    }

    .ds-icon {
      flex-shrink: 0;
      color: ${theme.colors.primary.base};
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .ds-text {
      min-width: 0;
      line-height: 1.35;
    }

    .dataset-name {
      display: flex;
      align-items: center;
      gap: 4px;
      color: ${theme.colors.primary.base};
      font-size: 14px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      line-height: 1.2;
    }

    .ext-icon {
      flex-shrink: 0;
      font-size: 10px;
      opacity: 0.5;
    }

    .database-name {
      display: block;
      font-size: 11px;
      color: ${theme.colors.grayscale.base};
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `}
`;

function DatasetIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      aria-hidden
    >
      <ellipse cx="12" cy="6" rx="8" ry="3" />
      <path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6" />
      <path d="M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
    </svg>
  );
}

export function parseDatasourceName(fullName: string): {
  database: string;
  table: string;
} {
  const parts = fullName?.split('.') || [];
  if (parts.length > 1) {
    return {
      database: parts[0],
      table: parts.slice(1).join('.'),
    };
  }
  return {
    database: '',
    table: fullName || '',
  };
}

export default function DatasourceCell({
  name,
  dataset,
  database,
  showIcon = true,
  showExtIcon = true,
}: DatasourceCellProps) {
  let tableName = dataset;
  let dbName = database;

  if (!tableName && name) {
    const parsed = parseDatasourceName(name);
    tableName = parsed.table;
    dbName = dbName || parsed.database;
  }

  return (
    <DatasourceCellWrapper className="list-datasource-cell">
      {showIcon && (
        <span className="ds-icon">
          <DatasetIcon />
        </span>
      )}
      <div className="ds-text">
        <span className="dataset-name">
          {tableName}
          {showExtIcon && (
            <span className="ext-icon" aria-hidden>
              ↗
            </span>
          )}
        </span>
        {dbName && <span className="database-name">{dbName}</span>}
      </div>
    </DatasourceCellWrapper>
  );
}
